// Event Sales CSV exports and result copy (Claude Design (4), CMP Dashboard
// D:9959-10013). The full attendee and sponsor lists come from the backend;
// the filtered attendee list is built here from the rows the table shows.

export const eventAttendeeListCsvFilename = "attendee-list.csv";
export const eventFilteredAttendeeListCsvFilename =
  "attendee-list-filtered.csv";
export const eventSponsorListCsvFilename = "sponsor-list.csv";

export const eventFilteredAttendeeCsvHeaders = [
  "Invoice #",
  "Attendee Name",
  "Company Name",
  "Ticket Type",
  "Qty",
  "Invoice Status",
] as const;

export type EventFilteredAttendeeCsvRow = Readonly<{
  attendeeName: string;
  companyName: string;
  invoiceNumber: string;
  invoiceStatus: string;
  quantity: number;
  ticketType: string;
}>;

// Same guard as the Emails and Mass Credentials exports: a cell that starts
// with =, +, - or @ (after optional whitespace) is prefixed with ' so a
// spreadsheet program shows it as text instead of running it as a formula.
export const toSafeEventCsvCell = (value: string | number): string => {
  const text = String(value);
  const safe = /^[\s]*[=+@-]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

export const buildEventFilteredAttendeeCsv = (
  rows: readonly EventFilteredAttendeeCsvRow[],
): string =>
  [
    eventFilteredAttendeeCsvHeaders.map(toSafeEventCsvCell).join(","),
    ...rows.map((row) =>
      [
        row.invoiceNumber,
        row.attendeeName,
        row.companyName,
        row.ticketType,
        row.quantity,
        row.invoiceStatus,
      ]
        .map(toSafeEventCsvCell)
        .join(","),
    ),
  ].join("\r\n");

export type EventSalesSuccessMessage = Readonly<{
  highlights: readonly string[];
  message: string;
}>;

export const getEventAttendeeListExportSuccess =
  (): EventSalesSuccessMessage => ({
    message: `A .csv file (${eventAttendeeListCsvFilename}) has been downloaded with the list of all the attendees!`,
    highlights: [eventAttendeeListCsvFilename],
  });

export const getEventFilteredAttendeeListExportSuccess =
  (): EventSalesSuccessMessage => ({
    message: `A .csv file (${eventFilteredAttendeeListCsvFilename}) has been downloaded with the filtered list of attendees!`,
    highlights: [eventFilteredAttendeeListCsvFilename],
  });

export const getEventSponsorListExportSuccess = (
  eventName: string,
): EventSalesSuccessMessage => ({
  message: `A .csv file (${eventSponsorListCsvFilename}) has been downloaded with the sponsor list for ${eventName}!`,
  highlights: [eventSponsorListCsvFilename, eventName],
});

export const eventSalesChangesSavedMessage =
  "Your changes have been saved successfully!";

// Update Attendee Details helper (D:13162-13164): the copy depends on
// whether the ticket or sponsorship invoice is still awaiting payment.
export const getEventAttendeeUpdateHelperText = ({
  awaitingPayment,
  kind,
}: {
  awaitingPayment: boolean;
  kind: "ticket" | "sponsorship";
}): string =>
  awaitingPayment
    ? `Update the name, email, and phone number for each attendee listed. Tickets will be issued and emailed to the following attendees once the ${
        kind === "sponsorship" ? "Sponsorship" : "Ticket Invoice"
      } has been paid.`
    : "Update the name, email and phone number for each attendee. Since the tickets have already been issued, these changes are for internal tracking only. Tickets will not be reissued and emailed to the new attendee.";
